import React, { useState, useEffect } from 'react'
import GoogleLogin, { GoogleAuthenticateSuccess } from '#src/components/GoogleLogin'
import { TextField } from '@deal/components'
import { usePathContext } from '#src/components/Path/PathContext'
import { OAuthEmailPathFlowQuestionDefinition } from '#src/components/Path/Question'
import styles from './styles.css'
import { isEmailValid } from '#src/services/email'
import { isFirstNameValid } from '#src/services/name'

const OAuthEmailQuestion: React.SFC<OAuthEmailPathFlowQuestionDefinition<{}>> = ({
  question,
  onFieldValueChanged,
  onFieldValidityChanged
}) => {
  const pathContext = usePathContext()
  const [isEntireQuestionValid, setIsEntireQuestionValid] = useState(false)
  const [isEmailFieldValid, setIsEmailFieldValid] = useState(
    question.oAuthEmailUserInput.email ? isEmailValid(question.oAuthEmailUserInput.email) : false
  )
  const [isFirstNameFieldValid, setIsFirstNameFieldValid] = useState(
    question.oAuthEmailUserInput.firstName
      ? isFirstNameValid(question.oAuthEmailUserInput.firstName)
      : false
  )

  /**
   * One time assertion of the questions initial state
   */
  useEffect(() => {
    if (question.oAuthEmailUserInput.email) {
      onFieldValueChanged({
        email: question.oAuthEmailUserInput.email
      })
    }

    if (question.oAuthEmailUserInput.firstName) {
      onFieldValueChanged({
        firstName: question.oAuthEmailUserInput.firstName
      })
    }

    if (!question.required || (isEmailFieldValid && isFirstNameFieldValid)) {
      onFieldValidityChanged(true)
      setIsEntireQuestionValid(true)
    }
  }, [])

  /**
   * Monitor state changes to update question validity in form state and local state
   */
  useEffect(() => {
    if (isEmailFieldValid && isFirstNameFieldValid && !isEntireQuestionValid) {
      handleUpdateQuestionValidity()
    } else if ((!isEmailFieldValid || !isFirstNameFieldValid) && isEntireQuestionValid) {
      handleUpdateQuestionValidity()
    }
  })

  const handleGoogleSuccess = (response: GoogleAuthenticateSuccess) => {
    const googleIdToken = response.idToken!
    const firstName = response.user.givenName
    const lastName = response.user.familyName
    const email = response.user.email

    onFieldValueChanged({
      googleIdToken,
      firstName: firstName || '',
      skipFirstName: firstName ? 'YES' : 'NO',
      lastName: lastName || '',
      skipLastName: lastName ? 'YES' : 'NO',
      email: email || ''
    })

    setIsEmailFieldValid(!!email)
    setIsFirstNameFieldValid(!!firstName)

    pathContext.onPageSubmitted()
  }

  const handleUpdateQuestionValidity = () => {
    if (isEmailFieldValid && isFirstNameFieldValid) {
      setIsEntireQuestionValid(true)
      onFieldValidityChanged(true)
    } else {
      setIsEntireQuestionValid(false)
      onFieldValidityChanged(false)
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    onFieldValueChanged({
      [name]: value
    })

    /**
     * If the field is manually entered, check it against our
     * own regex rather than accept it blindly from the oauth
     */
    if (name === 'email') {
      setIsEmailFieldValid(isEmailValid(value))
    } else if (name === 'firstName') {
      setIsFirstNameFieldValid(isFirstNameValid(value))
    }
  }

  return (
    <span>
      <div className={styles.container}>
        <div className={styles.loginButtons}>
          <div>
            <GoogleLogin
              className={styles.loginButton}
              text="Continue with Google"
              onSuccess={handleGoogleSuccess}
            />
          </div>
          <div>
            <p className={styles.questionNote}>or</p>
          </div>
        </div>
        <div className={styles.row}>
          <p className={styles.emailTitle}>Continue with your name and email address</p>

          <div className={styles.inputRow}>
            <TextField
              label=""
              labelHidden={true}
              autoFocus={false}
              type="text"
              name="firstName"
              display="standard"
              onChange={handleChange}
              placeholder="First name"
              valid={isFirstNameFieldValid}
              defaultValue={question.oAuthEmailUserInput.firstName || undefined}
            />
          </div>

          <div className={styles.inputRow}>
            <TextField
              label=""
              labelHidden={true}
              autoFocus={false}
              type="email"
              name="email"
              display="standard"
              onChange={handleChange}
              placeholder="Email"
              valid={isEmailFieldValid}
              defaultValue={question.oAuthEmailUserInput.email || undefined}
            />
          </div>
        </div>
      </div>
    </span>
  )
}

export default OAuthEmailQuestion
